/**
 * Utilitários para detectar e recuperar de falhas de carregamento de chunk
 * (ChunkLoadError).
 *
 * Causa típica: após um deploy novo, o HTML em cache do navegador referencia
 * hashes de bundle (`/_next/static/chunks/*.js`) que não existem mais no
 * servidor. Ao usar uma rota cujo bundle mudou, o navegador recebe 404 no
 * `.js` e o Next.js derruba a árvore com a mensagem genérica
 * "Application error: a client-side exception has occurred".
 *
 * Esse erro acontece FORA do ciclo de render do React, então os boundaries
 * `error.tsx` não o capturam de forma confiável. A recuperação correta é
 * recarregar a página uma vez para buscar o HTML atualizado.
 */

/** Chave de sessão com o timestamp do último reload automático. */
const CHUNK_RELOAD_TIMESTAMP_KEY = 'siscount:chunk-reloaded-at';

/**
 * Janela de cooldown entre reloads automáticos. Reloads dentro dessa janela
 * são ignorados para prevenir loop quando a falha é persistente (CDN offline,
 * bug real de bundle). Reloads após a janela são permitidos para que o
 * recovery funcione em ocorrências intermitentes ao longo da sessão.
 */
const CHUNK_RELOAD_COOLDOWN_MS = 30 * 1000;

/**
 * Identifica se um valor de erro corresponde a uma falha de carregamento de
 * chunk de JavaScript/CSS do bundle.
 */
export function isChunkLoadError(value: unknown): boolean {
    if (!value) return false;
    const err = value as { name?: unknown; message?: unknown };
    const name = String(err.name ?? '');
    const message = String(err.message ?? '');
    return (
        name === 'ChunkLoadError' ||
        /Loading chunk [^\s]+ failed/i.test(message) ||
        /Loading CSS chunk [^\s]+ failed/i.test(message) ||
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /importing a module script failed/i.test(message)
    );
}

const readLastReloadTimestamp = (storage: Storage | null): number | null => {
    if (!storage) return null;
    try {
        const raw = storage.getItem(CHUNK_RELOAD_TIMESTAMP_KEY);
        if (!raw) return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
};

const writeLastReloadTimestamp = (storage: Storage | null, timestamp: number): void => {
    if (!storage) return;
    try {
        storage.setItem(CHUNK_RELOAD_TIMESTAMP_KEY, String(timestamp));
    } catch {
        // sessionStorage indisponível (modo privado / cookies bloqueados):
        // sem timestamp não dá pra evitar loop. Aceitamos esse risco — o
        // cooldown abaixo já protege casos comuns; falhas persistentes em
        // navegadores sem storage também são raras o suficiente para nao
        // justificar fallback adicional.
    }
};

/**
 * Decisao pura: dado um timestamp atual e um storage, retorna se a recuperacao
 * pode ser disparada agora (fora do cooldown). Quando retorna `true`, ja
 * persiste o timestamp para que a proxima chamada cai no cooldown ate a
 * janela expirar.
 *
 * Util para testes — nao toca `window.location` e nao assume ambiente browser.
 */
export function shouldRecoverFromChunkError(
    now: number,
    storage: Storage | null,
): boolean {
    const lastReload = readLastReloadTimestamp(storage);
    if (lastReload !== null && now - lastReload < CHUNK_RELOAD_COOLDOWN_MS) {
        return false;
    }
    writeLastReloadTimestamp(storage, now);
    return true;
}

/**
 * Recarrega a página para buscar o HTML/bundle atualizado, respeitando um
 * cooldown que impede loop quando a falha é persistente.
 *
 * Diferente da implementação antiga (1 reload por sessão inteira), aqui o
 * cooldown é por janela de tempo: depois de 30s sem novo erro, a recuperação
 * volta a estar disponível. Isso garante que um ChunkLoadError intermitente
 * (intervalos entre uso) sempre dispare reload, em vez de mostrar a tela de
 * erro ao usuário só porque já houve uma recuperação anterior na sessão.
 *
 * @returns `true` se disparou o reload; `false` se está dentro do cooldown
 *          ou executado fora do navegador.
 */
export function recoverFromChunkError(): boolean {
    if (typeof window === 'undefined') return false;

    const storage: Storage | null = (() => {
        try {
            return window.sessionStorage;
        } catch {
            return null;
        }
    })();

    if (!shouldRecoverFromChunkError(Date.now(), storage)) {
        return false;
    }

    window.location.reload();
    return true;
}

/**
 * Constantes exportadas para uso em testes e em consumidores que precisam
 * implementar a mesma política de cooldown (ex.: boundary que faz reload).
 */
export const CHUNK_RELOAD_COOLDOWN = CHUNK_RELOAD_COOLDOWN_MS;
export const CHUNK_RELOAD_TIMESTAMP_STORAGE_KEY = CHUNK_RELOAD_TIMESTAMP_KEY;
